import { useEffect } from 'react'

export const SITE_NAME = 'Noctuzan'
export const DEFAULT_SHARE_IMAGE =
  'https://cdn.magicpatterns.com/patterns/generated-images/dcac1858-57d7-4548-9528-06e745dc379d.jpg'

interface PageMeta {
  /** Page-specific title; the site name is appended automatically. */
  title: string
  description: string
  /** Absolute image URL used for link previews. Falls back to the brand card. */
  image?: string
  /** Route path used to build the canonical/og:url. */
  path: string
  /** Extra JSON-LD structured data for the page, merged with the site node. */
  structuredData?: Record<string, unknown>
}

function upsertTag(selector: string, create: () => HTMLElement, attr: string, value: string): void {
  let el = document.head.querySelector<HTMLElement>(selector)
  if (!el) {
    el = create()
    document.head.appendChild(el)
  }
  el.setAttribute(attr, value)
}

function setMetaByName(name: string, content: string): void {
  upsertTag(
    `meta[name="${name}"]`,
    () => {
      const el = document.createElement('meta')
      el.setAttribute('name', name)
      return el
    },
    'content',
    content,
  )
}

function setMetaByProperty(property: string, content: string): void {
  upsertTag(
    `meta[property="${property}"]`,
    () => {
      const el = document.createElement('meta')
      el.setAttribute('property', property)
      return el
    },
    'content',
    content,
  )
}

function setLink(rel: string, href: string): void {
  upsertTag(
    `link[rel="${rel}"]`,
    () => {
      const el = document.createElement('link')
      el.setAttribute('rel', rel)
      return el
    },
    'href',
    href,
  )
}

/**
 * Per-page document head: title, description, canonical URL, Open Graph and
 * Twitter card tags, plus JSON-LD structured data — so a shared link shows the
 * right name, blurb and artwork instead of a bare URL.
 */
export function usePageMeta({ title, description, image, path, structuredData }: PageMeta): void {
  useEffect(() => {
    const fullTitle = title.startsWith(SITE_NAME) ? title : `${title} · ${SITE_NAME}`
    const url = `${window.location.origin}${path}`
    const shareImage = image || DEFAULT_SHARE_IMAGE

    document.title = fullTitle
    document.documentElement.lang = 'en'

    setMetaByName('description', description)
    setMetaByName('theme-color', '#0D0B14')
    setMetaByName('robots', 'index, follow')
    setLink('canonical', url)

    setMetaByProperty('og:site_name', SITE_NAME)
    setMetaByProperty('og:type', 'website')
    setMetaByProperty('og:title', fullTitle)
    setMetaByProperty('og:description', description)
    setMetaByProperty('og:url', url)
    setMetaByProperty('og:image', shareImage)
    setMetaByProperty('og:image:alt', `${SITE_NAME} — ${title}`)

    setMetaByName('twitter:card', 'summary_large_image')
    setMetaByName('twitter:title', fullTitle)
    setMetaByName('twitter:description', description)
    setMetaByName('twitter:image', shareImage)

    const ldId = 'noctuzan-structured-data'
    let script = document.getElementById(ldId) as HTMLScriptElement | null
    if (!script) {
      script = document.createElement('script')
      script.id = ldId
      script.type = 'application/ld+json'
      document.head.appendChild(script)
    }
    script.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'WebSite',
          name: SITE_NAME,
          url: window.location.origin,
          description: 'Late-night radio, on demand — real previews from the iTunes catalogue.',
        },
        {
          '@type': 'WebPage',
          name: fullTitle,
          url,
          description,
          primaryImageOfPage: shareImage,
        },
        ...(structuredData ? [structuredData] : []),
      ],
    })
  }, [description, image, path, structuredData, title])
}
